/**
 * Classement des réservations dans la timeline du restaurateur (Aujourd'hui / Demain /
 * Cette semaine / Passées). Fonctions pures, en heure locale du restaurant.
 */

export type SectionKey = 'today' | 'tomorrow' | 'week' | 'past';

export const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
export const endOfDay = (d: Date) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
export const addDays = (d: Date, days: number) => { const x = new Date(d); x.setDate(x.getDate() + days); return x; };
export const addHours = (d: Date, hours: number) => new Date(d.getTime() + hours * 3_600_000);
export const endOfWeek = (d: Date) => {
    const x = endOfDay(d);
    const day = x.getDay(); // 0 = dimanche
    // Le dimanche, « Cette semaine » va jusqu'au dimanche suivant (pas seulement aujourd'hui).
    const daysUntilSunday = day === 0 ? 7 : 7 - day;
    return endOfDay(addDays(x, daysUntilSunday));
};

/** Une table, ou une offre flash sur place, reste « Aujourd'hui » (en service) jusqu'à 3 h après l'heure réservée. */
export const SERVICE_HOURS = 3;

/**
 * Date et heure de la réservation, en heure locale du restaurant. Construite champ par champ :
 * `new Date('AAAA-MM-JJ')` serait lu en UTC (la veille dans les fuseaux à l'ouest de Greenwich).
 */
export const parseReservationDate = (date: string, time: string): Date | null => {
    const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(date || '');
    if (!d) return null;
    const t = /^(\d{1,2}):(\d{2})/.exec(time || '');
    const when = new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), t ? Number(t[1]) : 0, t ? Number(t[2]) : 0);
    return isNaN(when.getTime()) ? null : when;
};

export interface SectionInput {
    type: 'table' | 'flash';
    status: string;
    when: Date | null;
    /** Offre flash : le service de 3 h ne vaut que sur place (absent = à emporter). */
    fulfillment?: 'takeaway' | 'dine_in';
}

/** Section d'une réservation à l'instant `now`. */
export function reservationSection(r: SectionInput, now: Date): SectionKey {
    const { when } = r;
    if (!when) return 'past';
    // Table en service : l'heure est passée mais les clients sont encore là.
    const seated = r.type === 'table' || r.fulfillment === 'dine_in';
    const inService = seated && (r.status === 'confirmed' || r.status === 'pending')
        && when < now && now < addHours(when, SERVICE_HOURS);
    if (inService) return 'today';
    if (when < now) return 'past';
    if (when <= endOfDay(now)) return 'today';
    const tomorrow = addDays(now, 1);
    if (when >= startOfDay(tomorrow) && when <= endOfDay(tomorrow)) return 'tomorrow';
    return 'week'; // à partir de J+2, y compris au-delà de la fin de semaine
}

/**
 * Prochain instant où une section peut changer (heure atteinte, fin de service, minuit),
 * au plus tard dans `maxWaitMs` : le minuteur de bascule réveille l'écran à ce moment.
 */
export function nextSectionChange(whens: Array<Date | null>, nowMs: number, maxWaitMs = 15 * 60_000): number {
    const boundaries = [startOfDay(addDays(new Date(nowMs), 1)).getTime(), nowMs + maxWaitMs];
    for (const w of whens) {
        if (!w) continue;
        boundaries.push(w.getTime(), addHours(w, SERVICE_HOURS).getTime());
    }
    return Math.min(...boundaries.filter(b => b > nowMs));
}
